/** Phase B go-live switches.
 *
 * Each entry turns ONE real-world action from OFF (Phase A — the button shows
 * "— Phase B" and cannot fire) to ON. They ship OFF; we flip them in the
 * supervised sit-down, one at a time, verifying each before the next.
 *
 * A per-device override lets us enable a single action on ONE machine without a
 * deploy: localStorage `LIVEFIRE_<action> = "1"`. The smoke tests use it, and
 * it lets us prove an action live on Tom's machine during the session, then
 * make it permanent with the code flip below. It only enables the UI path — the
 * action still needs a real backend + a valid login — so it is NOT a security
 * boundary (the backend authenticates every call regardless).
 *
 * Why a switch at all (and not just always-on): during the parallel run v1 is
 * the live business. A stray click on beta must not fire a real text, email, or
 * Drive write until we deliberately turn it on together. */
export type LiveAction =
  | "emailSend" | "driveUpload" | "driveFolders" | "addProperty" | "settingsEdit"
  | "contractorsEdit" | "expensesEdit" | "scrapersEdit" | "dealInbox" | "sellingAi"
  | "quoSend" | "quoCall" | "qboSync"
  // Engine / automation (Task #47/#48) — every one ships OFF. These are the
  // background or bulk real-fire actions that must NOT double-run against the
  // shared DB while v1 is live; flip each in the supervised go-live, one at a time.
  | "enrich"          // lead enrichment writes (market/county/flood/permits pulls → edits)
  | "notifyPoll"      // periodic notification refetch (read-only, but off to spare backend load in parallel run)
  | "dealInboxScan"   // scheduled/background Gmail deal-inbox auto-scan (creates leads)
  | "dealEngine"      // deal-engine outcome sweep / auto-advance / auto-agent-contact
  | "textAgentSend"   // Text Agent outreach / drip sending (real SMS)
  | "materialsPricing"; // live materials price pulls

/** Permanent state, flipped by code at go-live (one line per action).
 * GO-LIVE 2026-07-19: flipped ON after the supervised sit-down proved each path
 * live (settings write + email read/send verified on real data; Drive confirmed
 * connected; email/drive/folders share the proven backend + honest send paths).
 * Text Agent real-SMS is NOT here — it's gated at AgentPage and stays off until
 * its own one-text-to-Tom proof. Deal Inbox parse/scan CREATE real leads and the
 * scan needs a connected Gmail (OAuth), so `dealInbox` also ships OFF until its
 * supervised enable — flip LIVEFIRE_dealInbox=1 on one machine to prove it. */
const DEFAULT: Record<LiveAction, boolean> = {
  emailSend: true,
  driveUpload: true,
  driveFolders: true,
  addProperty: true,
  settingsEdit: true,
  contractorsEdit: true,
  expensesEdit: true,
  scrapersEdit: true,
  // dealInbox (2026-07-23 go-live item 4): ON. Manual scan-now + paste/photo
  // parse — the SAME actions v1 offers today, and the server cron already
  // scans Gmail on a schedule regardless. Creates leads only; sends nothing.
  dealInbox: true,
  // sellingAi (2026-07-22, Tom: "this tab doesn't have the same functions as
  // v1"): ON. These are parse/generate calls only — addendum-parse and
  // invoice-parse read an uploaded PDF and return JSON, remarks returns text.
  // No external send, no Drive write; the results land as ordinary deal-edit
  // door writes (same as typing them). v1 fires these same endpoints today.
  sellingAi: true,
  // Real-fire comms/accounting — ship OFF, prove in the supervised sit-down
  // (a stray click must never text a buyer, place a call, or push to QuickBooks
  // while v1 is the live business). Reading threads / status / backups is safe
  // and NOT gated. Flip LIVEFIRE_quoSend=1 on one machine to prove each.
  // quoSend (2026-07-23 go-live item 5): ON — supervised proof is Tom texting
  // HIS OWN phone from the Messages page + lead drawer before any lead. Every
  // send is a deliberate composer action; nothing sends in bulk or on a timer
  // (textAgentSend stays OFF).
  quoSend: true,
  // quoCall (2026-07-23 go-live item 6): ON — flipped right after the SMS
  // self-proof passed both directions (Tom's own phone, QA TEST lead thread:
  // out 2:10 PM "sent" + reply "Test" 2:15 PM rendered in the drawer). Calls
  // are single deliberate clicks that ring TOM'S phone first, then bridge —
  // nothing dials in bulk or on a timer. Proof: Tom calls his own number from
  // the drawer's Messages & Calls card.
  quoCall: true,
  qboSync: false,
  // enrich (2026-07-23, Tom: "make sure enrich is working in properties"): ON.
  // These are pull-only calls — market-check / county-check / flood-check read
  // Zillow + the county and stamp the lead row server-side (the same class of
  // enrichment the scraper pipeline already runs nightly); nothing external is
  // sent. The client-side half lands as ordinary lead_fields door writes.
  enrich: true,
  // notifyPoll (2026-07-23 go-live item 3): ON. One read-only fetch per minute
  // so the bell ticks live — the same poll v1 runs; no writes, nothing fires.
  notifyPoll: true,
  // dealInboxScan (2026-07-23): LIVE — but the loop only runs when Tom's
  // VISIBLE "Auto-scan email" toggle (AUTOMATION.emailScan) is on AND a
  // deal-Gmail is connected. The gate being live just means that toggle now
  // does something; nothing scans until Tom flips the toggle himself. Creating
  // leads from his own inbox is reversible (leads soft-delete).
  dealInboxScan: true,
  // dealEngine (2026-07-23): LIVE — this drives INTERNAL scoring only (deScore
  // stamped into lead fields so v1 + lists read one number; no stage auto-
  // advance, no external contact). Auto-contacting sellers stays under
  // textAgentSend, which is still OFF pending the supervised send proof.
  dealEngine: true,
  // textAgentSend stays OFF — the ONLY gate governing real Quo sends from the
  // agent queue. Flips at the supervised watched-batch sit-down.
  textAgentSend: false,
  materialsPricing: false,
};

export function liveOn(action: LiveAction): boolean {
  if (DEFAULT[action]) return true;
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem("LIVEFIRE_" + action) === "1";
  } catch {
    return false;
  }
}
